"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { CheckCircle2, CircleAlert, CircleDashed, Clock3, ListChecks } from "lucide-react";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { ValerieCard } from "@/src/components/valerie-card";
import type { ReactNode } from "react";
export type ReferralWorkflowStatus = "automated" | "verified" | "verify" | "needs_action" | "failed" | "waiting" | "not_run" | "not_needed";

/** USRC copy of the Confluent presentation, which is itself a copy of TPR's pending the shared refactor. */
export type ReferralWorkflowStep = {
  title: string;
  status: ReferralWorkflowStatus;
  summary: string;
  content?: ReactNode;
};
export function ReferralWorkflowLayout(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex w-full max-w-2xl flex-col gap-8 sm:min-w-[800px] mx-auto">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
const STATUS_PRESENTATION = {
  automated: {
    label: "Automated",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />
  },
  verified: {
    label: "Verified",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />
  },
  verify: {
    label: "Verify",
    className: "border-warn/30 bg-warn/10 text-warn-foreground",
    icon: <CircleAlert />
  },
  needs_action: {
    label: "Needs action",
    className: "border-warn-foreground/30 bg-warn-foreground/10 text-warn-foreground",
    icon: <CircleAlert />
  },
  failed: {
    label: "Failed",
    className: "border-destructive-foreground/30 bg-destructive-foreground/10 text-destructive-foreground",
    icon: <CircleAlert />
  },
  waiting: {
    label: "Waiting",
    className: "border-border bg-muted text-muted-foreground",
    icon: <Clock3 />
  },
  not_run: {
    label: "Not run",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />
  },
  not_needed: {
    label: "Not needed",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />
  }
} satisfies Record<ReferralWorkflowStatus, {
  label: string;
  className: string;
  icon: ReactNode;
}>;
function ReferralWorkflowStepCard(t0) {
  const $ = _c(14);
  const {
    step
  } = t0;
  const status = STATUS_PRESENTATION[step.status];
  const isSkipped = step.status === "not_needed";
  const cardVariant = step.status === "automated" || step.status === "verified" ? "success" : step.status === "failed" ? "destructive" : step.status === "needs_action" || step.status === "verify" ? "warn" : "outline";
  const t1 = `${step.number}. ${step.title}`;
  const t2 = step.status === "automated" ? undefined : status.icon;
  const t3 = step.status === "not_run" || isSkipped ? "bg-muted/20 text-muted-foreground opacity-40 grayscale" : undefined;
  let t4;
  if ($[0] !== status || $[1] !== step.status) {
    t4 = step.status === "automated" ? <span className="text-success-foreground" aria-label="Automated"><CheckCircleIcon weight="fill" className="size-5" /></span> : <Badge variant="outline" className={status.className}>{status.label}</Badge>;
    $[0] = status;
    $[1] = step.status;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== isSkipped || $[4] !== step.content || $[5] !== step.summary) {
    t5 = isSkipped ? <details><summary className="cursor-pointer text-sm text-muted-foreground">{step.summary}</summary><div className="mt-3 space-y-3">{step.content}</div></details> : <><p className="text-sm text-muted-foreground">{step.summary}</p>{step.content}</>;
    $[3] = isSkipped;
    $[4] = step.content;
    $[5] = step.summary;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== cardVariant || $[8] !== t1 || $[9] !== t2 || $[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = <ValerieCard title={t1} icon={t2} className={t3} variant={cardVariant} actions={t4} contentClassName="space-y-3">{t5}</ValerieCard>;
    $[7] = cardVariant;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
export function ReferralWorkflowTimeline(t0) {
  const $ = _c(8);
  const {
    steps,
    isProcessing: t1,
    waitingMessage
  } = t0;
  const isProcessing = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== isProcessing || $[1] !== steps || $[2] !== waitingMessage) {
    const numberedSteps = steps.map(_temp);
    const actions = numberedSteps.filter(_temp2);
    const hasFailure = steps.some(_temp3);
    const summaryVariant = hasFailure ? "destructive" : actions.length || waitingMessage ? "warn" : isProcessing ? "outline" : "success";
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <ListChecks />;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    const t4 = waitingMessage ? <p className="text-sm">{waitingMessage}</p> : actions.length ? <ol className="space-y-2 text-sm">{actions.map(_temp4)}</ol> : <p className="text-sm">{isProcessing ? "Automation is still running. This timeline will update as each workflow step completes." : "No manual actions remain."}</p>;
    let t5;
    if ($[5] !== summaryVariant || $[6] !== t4) {
      t5 = <ValerieCard title="Actions remaining" icon={t3} variant={summaryVariant} contentClassName="space-y-4">{t4}</ValerieCard>;
      $[5] = summaryVariant;
      $[6] = t4;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    t2 = <div className="space-y-8 pb-8">{t5}{numberedSteps.map(_temp5)}</div>;
    $[0] = isProcessing;
    $[1] = steps;
    $[2] = waitingMessage;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp5(step_3) {
  return <ReferralWorkflowStepCard key={step_3.number} step={step_3} />;
}
function _temp4(step_2) {
  return <li key={step_2.number}><strong>Step {step_2.number}: {step_2.title}</strong><span className="block text-muted-foreground">{step_2.summary}</span></li>;
}
function _temp3(step_1) {
  return step_1.status === "failed";
}
function _temp2(step_0) {
  return ["needs_action", "failed", "verify"].includes(step_0.status);
}
function _temp(step, index) {
  return {
    ...step,
    number: index + 1
  };
}